import { showToast } from '@/composables/swal';
import { messageForError } from '@/utils/userMessage';
import { http } from '@/interceptors/axiosRequestInterceptor';
import type {
  BreadCrumb,
  CancelTokenMap,
  ContextMenu,
  DriveItem,
  DriveItemActionPayload,
  Factory,
  FactoryId,
  ItemAction,
  ItemActionInfo,
  MultiFileUpload,
  SelectionBox,
  SharePointFactoryInfoResponse,
  SharePointFilesResponse,
  SharePointSettings,
  ThumbnailMap,
  UploadPreparationMap,
  UploadProgressMap,
} from '@/types/sharepoint';
import axios from 'axios';
import { computed, reactive, ref, watch } from 'vue';
import { logError } from '.';
import { useVueQuery } from './useVueQuery';

export const useSharepointFiles = (orderId: FactoryId, orderFactories: Factory[]) => {
  // State
  const driveItems = ref<DriveItem[]>([]);
  const driveItemsParentInfo = ref<DriveItem['parent_info']>({
    path: '',
    id: '',
  });
  const driveBreadCrumbs = ref<BreadCrumb[]>([
    {
      isActive: true,
      name: 'Home',
      path: '',
    },
  ]);
  const activeFactory = ref<Factory>(orderFactories[0]);
  const sharePointDbSettingsNotFound = ref(false);
  const accessToken = ref<string>('');
  const sharePointSettings = ref<SharePointSettings>({});

  // Context Menu State
  const contextMenu = reactive<ContextMenu>({
    visible: false,
    style: {},
    item: null,
  });
  const contextMenuRef = ref<HTMLDivElement | null>(null);

  // Selection State
  const selectedFiles = ref<DriveItem[]>([]);
  const isSelecting = ref(false);
  const selectionBox = reactive<SelectionBox>({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });

  // Upload State
  const uploadProgress = ref<UploadProgressMap>({});
  const uploadPreparation = ref<UploadPreparationMap>({});
  const multiFileUpload = ref<MultiFileUpload>({
    total: 0,
    completed: 0,
    inProgress: false,
  });
  const cancelTokens = ref<CancelTokenMap>({});
  const isUploading = ref(false);

  // Action State
  const itemActionInfo = ref<ItemActionInfo | null>(null);
  const moveOperationInProgress = ref(false);
  const isMovingItems = ref(false);

  // Additional state for folder operations
  const newFolderName = ref('');
  const driveItemNewName = ref('');

  // Additional state
  const thumbnails = ref<ThumbnailMap>({});

  // Vue Query implementations
  const fetchSharePointFilesQuery = useVueQuery<SharePointFilesResponse['result']>(
    `sharepoint-files-${orderId}-${activeFactory.value.id}-${driveItemsParentInfo.value.path}`,
    async () => {
      try {
        const response = await http.post(`order/${orderId}/share/point/files`, {
          order_id: orderId,
          drive_items_path: driveItemsParentInfo.value.path || '/',
          factory_id: activeFactory.value.id,
        });

        if (!response.data?.result) {
          throw new Error('Invalid response format');
        }
        return response.data.result;
      } catch (error) {
        logError('Error fetching SharePoint files:', error);
        throw error;
      }
    },
    {
      enabled: !!orderId && !!activeFactory.value?.id,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const fetchFactorySharepointInfoQuery = useVueQuery<SharePointFactoryInfoResponse['result']>(
    `sharepoint-factory-info-${orderId}`,
    async () => {
      const factory_ids = orderFactories.map((factory) => factory.id);
      const response = await http.post('get-sharepoint-factory-info', {
        factory_ids,
      });
      return response.data.result;
    }
  );

  // Computed properties for processed data
  const processedDriveItems = computed(() => {
    if (!fetchSharePointFilesQuery.isSuccess.value || !fetchSharePointFilesQuery.data.value) {
      return null;
    }

    const data = fetchSharePointFilesQuery.data.value;
    return {
      parentInfo: {
        ...data.share_point_drive_item_parent_path_info,
        path: data.share_point_drive_item_parent_path_info.path || '/',
      },
      items: data.share_point_drive_path_items || [],
      breadcrumbs: data.drive_path_item_bread_crumbs || [
        {
          isActive: true,
          name: 'Home',
          path: '/',
        },
      ],
    };
  });

  const factoryInfo = computed(() => {
    if (
      !fetchFactorySharepointInfoQuery.isSuccess.value ||
      !fetchFactorySharepointInfoQuery.data.value
    ) {
      return null;
    }

    const data = fetchFactorySharepointInfoQuery.data.value;
    return {
      accessToken: data.access_token,
      settings: data.factories_sharepoint_settings[activeFactory.value.id],
    };
  });

  // Watch for processed data changes
  watch(
    processedDriveItems,
    (newData) => {
      if (newData) {
        driveItemsParentInfo.value = newData.parentInfo;
        driveItems.value = newData.items;
        driveBreadCrumbs.value = newData.breadcrumbs;
        // TOREVIEW: disabled files reset on directory change as copy and pasting was not working.
        // selectedFiles.value = []; // Reset selection when navigating
        sharePointDbSettingsNotFound.value = false;
      }
    },
    { immediate: true }
  );

  watch(
    factoryInfo,
    (newInfo) => {
      if (newInfo) {
        accessToken.value = newInfo.accessToken;
        sharePointSettings.value = newInfo.settings;
        sharePointDbSettingsNotFound.value = false;
      }
    },
    { immediate: true }
  );

  // Watch for query errors
  watch(
    [
      () => fetchSharePointFilesQuery.isError.value,
      () => fetchFactorySharepointInfoQuery.isError.value,
    ],
    ([isFilesError, isFactoryError]) => {
      if (isFilesError) {
        handleQueryError(fetchSharePointFilesQuery.error.value);
      }
      if (isFactoryError) {
        handleQueryError(fetchFactorySharepointInfoQuery.error.value);
      }
    }
  );

  // Watch for factory changes
  watch(
    () => activeFactory.value,
    (newFactory) => {
      if (newFactory) {
        // Reset to root path when changing factories
        driveItemsParentInfo.value = { path: '/', id: '' };
        fetchSharePointFilesQuery.refetch();
      }
    },
    { immediate: true }
  );

  // Error handling method
  const handleQueryError = (error: any) => {
    sharePointDbSettingsNotFound.value = true;
    driveItems.value = [];
    driveItemsParentInfo.value = { path: '', id: '' };
    driveBreadCrumbs.value = [];
    handleResponseException(error);
  };

  // Computed properties for loading states
  const isLoading = computed(
    () =>
      fetchSharePointFilesQuery.isFetching.value || fetchFactorySharepointInfoQuery.isFetching.value
  );
  const isOperationLoading = ref(false);

  // File Operations
  async function handleDriveItemAction(action: ItemAction) {
    isOperationLoading.value = true;
    let deletedCount = 0;
    let actionPayload: any;

    try {
      // 🔹 CUT / COPY — save selected items for later
      if (['cut', 'copy'].includes(action)) {
        const items =
          selectedFiles.value && selectedFiles.value.length > 0
            ? (selectedFiles.value as DriveItem[])
            : Array.isArray(contextMenu.item)
              ? (contextMenu.item as DriveItem[])
              : [(contextMenu.item as DriveItem)];

        itemActionInfo.value = {
          drive_item: items.length === 1 ? items[0] : items,
          action,
        };

        showToast(action === 'cut' ? 'Cut to clipboard.' : 'Copied to clipboard.', 'success');
        return;
      }

      // 🔹 Determine which items to process
      let itemsToProcess: DriveItem[] = [];
      if (action === 'paste') {
        if (!itemActionInfo.value?.drive_item) {
          showToast('Nothing to paste.', 'info');
          return;
        }

        itemsToProcess = Array.isArray(itemActionInfo.value.drive_item)
          ? (itemActionInfo.value.drive_item as DriveItem[])
          : [itemActionInfo.value.drive_item as DriveItem];
      } else if (action === 'new_folder') {
        // handled by your modal logic; we just prepare one "virtual" item for payload
        itemsToProcess = [];
      }
      else if (action === 'delete' || action === 'rename') {
        itemsToProcess =
          selectedFiles.value && selectedFiles.value.length > 0
            ? (selectedFiles.value as DriveItem[])
            : Array.isArray(contextMenu.item)
              ? (contextMenu.item as DriveItem[])
              : [(contextMenu.item as DriveItem)];
        itemActionInfo.value = {
          drive_item: itemsToProcess.length === 1 ? itemsToProcess[0] : itemsToProcess,
          action,
        };
      }
      else {
        // fallback: use selection or context menu
        itemsToProcess =
          selectedFiles.value && selectedFiles.value.length > 0
            ? (selectedFiles.value as DriveItem[])
            : contextMenu.item
              ? [contextMenu.item as DriveItem]
              : [];
      }

      if (!itemsToProcess.length && action !== 'new_folder') {
        showToast('Select an item first.', 'info');
        return;
      }

      if (action !== 'new_folder') {
        // 🔹 Execute SharePoint actions
        const promises = itemsToProcess.map(async (file) => {
          try {
            if (!file || !file.id) throw new Error('Invalid file reference');

            actionPayload = getDriveItemActionPayload(action, file);
            await handleSharepointDriveItemAction(actionPayload);

            if (action === 'delete') deletedCount++;
          } catch (err) {
            handleResponseException(err);
            throw err;
          }
        });
        await Promise.allSettled(promises);
      }
      else {
        try {
          actionPayload = getDriveItemActionPayload(action, {} as DriveItem);
          await handleSharepointDriveItemAction(actionPayload);

        } catch (err) {
          handleResponseException(err);
          throw err;
        }
      }


      // 🔹 Post-success UI updates
      if (action === 'delete') {
        showToast(`${deletedCount} item(s) deleted.`, 'success');
      } else if (action === 'paste') {
        showToast('Pasted.', 'success');
        itemActionInfo.value = null;
      } else if (action === 'rename') {
        showToast('Renamed.', 'success');
      } else if (action === 'move') {
        showToast('Moved.', 'success');
      } else if (action === 'new_folder') {
        showToast('Folder created.', 'success');
      }
      selectedFiles.value = [];
      await fetchSharePointFilesQuery.refetch();
    } catch (err) {
      handleResponseException(err);
    } finally {
      showRenameModal.value = false;
      showDeleteConfirm.value = false;
      isOperationLoading.value = false; // ✅ Loader always cleared
      showCreateFolderModal.value = false;
      newFolderName.value = "";
    }
  }




  const getDriveItemActionPayload = (
    action: ItemAction,
    file: DriveItem
  ): DriveItemActionPayload => {
    switch (action) {
      case 'new_folder':
        return {
          target_item_action: action,
          destination_item_id: driveItemsParentInfo.value.id,
          folder_name: newFolderName.value,
          factory_id: activeFactory.value.id,
        };
      case 'rename': {
        let driveItemFullName = driveItemNewName.value;
        if (
          itemActionInfo.value?.drive_item &&
          !Array.isArray(itemActionInfo.value.drive_item) &&
          itemActionInfo.value.drive_item.type === 'file'
        ) {
          driveItemFullName = `${driveItemFullName}.${file.name.split('.').pop()}`;
        }
        return {
          target_item_action: itemActionInfo.value?.action || action,
          drive_item_new_name: driveItemFullName,
          target_item_id: (itemActionInfo.value?.drive_item as DriveItem).id,
          factory_id: activeFactory.value.id,
        };
      }
      case 'paste':
        return {
          target_item_action: itemActionInfo.value?.action || action,
          target_item_id: file.id,
          destination_item_id: driveItemsParentInfo.value.id,
          factory_id: activeFactory.value.id,
        };
      case 'delete':
        return {
          target_item_action: itemActionInfo.value?.action || action,
          target_item_id: file.id,
          factory_id: activeFactory.value.id,
        };
      default:
        return {
          target_item_action: action,
          factory_id: activeFactory.value.id,
        };
    }
  };

  // Upload Operations
  const uploadSharepointFile = async (payload: {
    destination_item_id: string;
    file_name: string;
    file: File;
  }) => {
    const drive_id = sharePointSettings.value.drive_id;
    const drive_destination_item_id = payload.destination_item_id;
    const file_name = encodeURIComponent(payload.file_name);

    const site_url = `drives/${drive_id}/items/${drive_destination_item_id}:/${file_name}:/content`;
    const site_full_url = `${import.meta.env.VITE_APP_SHARPOINT_END_POINT}/${site_url}`;

    const cancelTokenSource = axios.CancelToken.source();
    setCancelToken(payload.file_name, cancelTokenSource);

    try {
      const response = await axios({
        method: 'put',
        url: site_full_url,
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
          'Content-Type': 'application/octet-stream',
        },
        data: payload.file,
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          updateUploadProgress(payload.file_name, progress);
        },
        cancelToken: cancelTokenSource.token,
      });

      updateUploadProgress(payload.file_name, 100);

      setTimeout(() => {
        clearFileUploadProgress(payload.file_name);
      }, 1500);

      incrementCompletedFiles();
      return response;
    } catch (error) {
      incrementCompletedFiles();
      handleResponseException(error);

      setTimeout(() => {
        clearFileUploadProgress(payload.file_name);
      }, 3000);

      throw error;
    }
  };

  // Utility Functions
  const handleResponseException = (error: any) => {
    logError('API Error:', error);
    showToast(messageForError(error), 'error');
  };

  // State Management Functions
  const updateUploadProgress = (fileName: string, progress: number) => {
    uploadProgress.value = {
      ...uploadProgress.value,
      [fileName]: progress,
    };
  };

  const clearFileUploadProgress = (fileName: string) => {
    const newUploadProgress = { ...uploadProgress.value };
    delete newUploadProgress[fileName];
    uploadProgress.value = newUploadProgress;
  };

  const setMultiFileUpload = (total: number) => {
    multiFileUpload.value = {
      total,
      completed: 0,
      inProgress: true,
    };
  };

  const incrementCompletedFiles = () => {
    multiFileUpload.value.completed++;
    if (multiFileUpload.value.completed >= multiFileUpload.value.total) {
      multiFileUpload.value.inProgress = false;
    }
  };

  const setCancelToken = (fileName: string, cancelToken: any) => {
    cancelTokens.value = { ...cancelTokens.value, [fileName]: cancelToken };
  };

  const handleSharepointDriveItemAction = async (payload: DriveItemActionPayload) => {
    if (!sharePointSettings.value.site_name) {
      sharePointDbSettingsNotFound.value = true;
      throw new Error('SharePoint settings not found');
    }

    const api_meta_data = await getSharepointDriveActionItemMetaData(payload);
    return await getSharepointApiData(api_meta_data);
  };

  const handleDriveItemActionCompletion = async (
    action: ItemAction,
    actionPayload: DriveItemActionPayload
  ) => {
    try {
      switch (action) {
        case 'new_folder':
          newFolderName.value = '';
          showCreateFolderModal.value = false;
          await fetchSharePointFilesQuery.refetch();
          break;
        case 'rename':
          if (itemActionInfo.value?.drive_item) {
            if (Array.isArray(itemActionInfo.value.drive_item)) {
              // Handle multiple items rename
              itemActionInfo.value.drive_item.forEach((item) => {
                if (item.type === 'file') {
                  const extension = item.name.split('.').pop() || '';
                  item.name = `${actionPayload.drive_item_new_name}.${extension}`;
                } else {
                  item.name = actionPayload.drive_item_new_name || '';
                }
              });
            } else {
              // Handle single item rename
              if (itemActionInfo.value.drive_item.type === 'file') {
                const extension = itemActionInfo.value.drive_item.name.split('.').pop() || '';
                itemActionInfo.value.drive_item.name = `${actionPayload.drive_item_new_name}.${extension}`;
              } else {
                itemActionInfo.value.drive_item.name = actionPayload.drive_item_new_name || '';
              }
            }
          }
          showRenameModal.value = false;
          await fetchSharePointFilesQuery.refetch();
          break;
        case 'delete':
          showDeleteConfirm.value = false;
          selectedFiles.value = [];
          await fetchSharePointFilesQuery.refetch();
          break;
        case 'copy':
        case 'cut':
        case 'paste':
          selectedFiles.value = [];
          await fetchSharePointFilesQuery.refetch();
          break;
        case 'move':
          selectedFiles.value = [];
          await fetchSharePointFilesQuery.refetch();
          break;
        default:
          await fetchSharePointFilesQuery.refetch();
      }

      // Clear any action info after completion
      itemActionInfo.value = null;
    } catch (error) {
      logError('Error in handleDriveItemActionCompletion:', error);
      handleResponseException(error);
    }
  };

  const getSharepointDriveActionItemMetaData = async (payload: DriveItemActionPayload) => {
    const drive_id = sharePointSettings.value.drive_id;
    const target_item_action = payload.target_item_action;
    const drive_target_item_id = payload.target_item_id;
    const drive_destination_item_id = payload.destination_item_id;
    const handle_conflict = payload.handle_conflict;

    const base_url = `drives/${drive_id}/items/${drive_target_item_id}`;
    const conflictParam = handle_conflict ? '@microsoft.graph.conflictBehavior=rename' : '';

    switch (target_item_action) {
      case 'copy':
        return {
          url: `${base_url}/copy?@microsoft.graph.conflictBehavior=rename`,
          api_payload: {
            parentReference: {
              id: drive_destination_item_id,
              driveId: drive_id,
            },
          },
          api_action: 'post',
        };
      case 'cut':
        return {
          url: `${base_url}${conflictParam}`,
          api_payload: {
            parentReference: {
              id: drive_destination_item_id,
              driveId: drive_id,
            },
          },
          api_action: 'patch',
        };
      case 'move':
        return {
          url: `${base_url}${conflictParam}`,
          api_payload: {
            parentReference: {
              id: drive_destination_item_id,
              driveId: drive_id,
            },
          },
          api_action: 'patch',
        };
      case 'delete':
        return {
          url: `${base_url}`,
          api_action: 'delete',
        };
      case 'rename':
        return {
          url: `${base_url}`,
          api_payload: {
            name: payload.drive_item_new_name,
          },
          api_action: 'patch',
        };
      case 'new_folder':
        return {
          url: `drives/${drive_id}/items/${drive_destination_item_id}/children`,
          api_payload: {
            name: payload.folder_name,
            folder: {},
          },
          api_action: 'post',
          query_params: '@microsoft.graph.conflictBehavior=rename',
        };
      default:
        return {};
    }
  };

  const getSharepointApiData = async (payload: any) => {
    if (!accessToken.value) {
      sharePointDbSettingsNotFound.value = true;
      throw new Error('SharePoint access token not found');
    }

    const api_payload = payload.api_payload;
    const api_action = payload.api_action;

    const site_url = payload.url;
    const site_full_url = `${import.meta.env.VITE_APP_SHARPOINT_END_POINT}/${site_url}`;

    try {
      const response = await axios({
        method: api_action,
        url: site_full_url,
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
          Accept: 'application/json',
        },
        data: api_payload,
      });

      return response.data;
    } catch (error) {
      handleResponseException(error);
      throw error;
    }
  };

  // Additional state for modals
  const showCreateFolderModal = ref(false);
  const showDeleteConfirm = ref(false);
  const showRenameModal = ref(false);

  // Additional functions
  const resetMultiFileUpload = () => {
    multiFileUpload.value = {
      total: 0,
      completed: 0,
      inProgress: false,
    };
  };

  const getFileThumbnails = async (driveItem: DriveItem) => {
    if (thumbnails.value[driveItem.id]) {
      return thumbnails.value[driveItem.id];
    }

    try {
      const drive_id = sharePointSettings.value.drive_id;
      const url = `${import.meta.env.VITE_APP_SHARPOINT_END_POINT}/drives/${drive_id}/items/${driveItem.id}/thumbnails`;

      const response = await axios({
        method: 'get',
        url: url,
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data && response.data.value && response.data.value.length > 0) {
        const thumbnailData = response.data.value[0];
        thumbnails.value = {
          ...thumbnails.value,
          [driveItem.id]: thumbnailData,
        };
        return thumbnailData;
      }
      return null;
    } catch (error) {
      logError('Error fetching thumbnails:', error);
      return null;
    }
  };

  const moveSharepointItem = async (
    itemId: string,
    destinationFolderId: string,
    handleConflict = false
  ) => {
    if (!sharePointSettings.value.site_name) {
      sharePointDbSettingsNotFound.value = true;
      throw new Error('SharePoint settings not found');
    }

    const payload: DriveItemActionPayload = {
      target_item_action: 'move',
      target_item_id: itemId,
      destination_item_id: destinationFolderId,
      handle_conflict: handleConflict,
      factory_id: activeFactory.value.id,
    };

    const api_meta_data = await getSharepointDriveActionItemMetaData(payload);
    return await getSharepointApiData(api_meta_data);
  };

  const removeCancelToken = (fileName: string) => {
    const newCancelTokens = { ...cancelTokens.value };
    delete newCancelTokens[fileName];
    cancelTokens.value = newCancelTokens;
  };

  const uploadLargeSharepointFile = async (payload: any) => {
    const drive_id = sharePointSettings.value.drive_id;
    const drive_destination_item_id = payload.destination_item_id;
    const file_name = payload.file_name;
    const file = payload.file;

    setUploadPreparation(file_name, true);

    const createSessionUrl = `${import.meta.env.VITE_APP_SHARPOINT_END_POINT}/drives/${drive_id}/items/${drive_destination_item_id}:/${file_name}:/createUploadSession`;

    try {
      const sessionResponse = await axios({
        method: 'post',
        url: createSessionUrl,
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
          'Content-Type': 'application/json',
        },
        data: {
          '@microsoft.graph.conflictBehavior': 'rename',
          name: file_name,
        },
      });

      const uploadUrl = sessionResponse.data.uploadUrl;
      return uploadFileInChunks(file, uploadUrl, file_name);
    } catch (error) {
      clearUploadPreparation(file_name);
      handleResponseException(error);
      throw error;
    }
  };

  const uploadFileInChunks = async (file: File, uploadUrl: string, fileName: string) => {
    const chunkSize = 5 * 1024 * 1024;
    const fileSize = file.size;
    let start = 0;
    let end = Math.min(chunkSize, fileSize);

    const cancelTokenSource = axios.CancelToken.source();
    setCancelToken(fileName, cancelTokenSource);

    while (start < fileSize) {
      const chunk = file.slice(start, end);
      const contentRange = `bytes ${start}-${end - 1}/${fileSize}`;

      try {
        const response = await axios.put(uploadUrl, chunk, {
          headers: {
            'Content-Range': contentRange,
          },
          maxRedirects: 0,
          cancelToken: cancelTokenSource.token,
        });

        const locationHeader = response.headers['location'];
        if (locationHeader) {
          const statusResponse = await axios.get(locationHeader, {
            headers: {
              Authorization: `Bearer ${accessToken.value}`,
            },
            cancelToken: cancelTokenSource.token,
          });

          if (statusResponse.data.nextExpectedRanges) {
            [start] = statusResponse.data.nextExpectedRanges;
            end = Math.min(start + chunkSize, fileSize);
          } else {
            updateUploadProgress(fileName, 100);
            removeCancelToken(fileName);
            clearUploadPreparation(fileName);

            setTimeout(() => {
              clearFileUploadProgress(fileName);
            }, 1500);

            return statusResponse.data;
          }
        } else {
          if (response.data.nextExpectedRanges) {
            [start] = response.data.nextExpectedRanges;
            end = Math.min(start + chunkSize, fileSize);
          } else {
            updateUploadProgress(fileName, 100);
            removeCancelToken(fileName);
            clearUploadPreparation(fileName);

            setTimeout(() => {
              clearFileUploadProgress(fileName);
            }, 1500);

            return response.data;
          }
        }

        const progress = Math.min(99, (start / fileSize) * 100);
        updateUploadProgress(fileName, progress);

        if (start > 0) {
          clearUploadPreparation(fileName);
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          removeCancelToken(fileName);
          clearFileUploadProgress(fileName);
          return { canceled: true };
        } else if (error.response && error.response.status === 302) {
          uploadUrl = error.response.headers['location'];
        } else {
          removeCancelToken(fileName);
          handleResponseException(error);
          throw error;
        }
      }
    }

    updateUploadProgress(fileName, 100);
    removeCancelToken(fileName);
    clearUploadPreparation(fileName);

    setTimeout(() => {
      clearFileUploadProgress(fileName);
    }, 1500);
  };

  // get SharePoint File Logs
  /**
   * AB#21539 — search the open folder and everything under it. Answers the
   * matching items formatted like a listing (each carries its parent_info.path).
   * Empty query → empty list, no request.
   */
  const searchSharepointFiles = async (query: string): Promise<DriveItem[]> => {
    const q = query.trim();
    if (!q) return [];
    const response = await http.post(`order/${orderId}/share/point/files/search`, {
      order_id: orderId,
      drive_items_path: driveItemsParentInfo.value.path || '/',
      factory_id: activeFactory.value.id,
      q,
    });
    return (response.data?.result?.share_point_drive_path_items ?? []) as DriveItem[];
  };

  /**
   * AB#21539 — server-rendered preview for what Graph cannot thumbnail
   * (.cdr, .plt, .eps). Resolves a Blob, or null when the API answers 204.
   */
  const getServerPreview = async (driveItem: DriveItem): Promise<Blob | null> => {
    const response = await http.get(`order/${orderId}/share/point/files/${driveItem.id}/preview`, {
      params: { factory_id: activeFactory.value.id },
      responseType: 'blob',
    });
    if (response.status !== 200 || !response.data || !(response.data as Blob).size) return null;
    return response.data as Blob;
  };

  const getFileLogs = async (driveItem: DriveItem) => {
    try {
      const fileActivityLogResponse = await getSharepointApiData({
        url: `drives/${sharePointSettings.value.drive_id}/items/${driveItem.id}/activities`,
        api_action: 'get',
        api_payload: {},
      });

      let updatedParentActivityLog = [];

      // if(driveItem.parent_info.id && driveItem.parent_info.id !== 'root'){
      //   const parentFolderActivityLogResponse = await getSharepointApiData({
      //     url: `drives/${sharePointSettings.value.drive_id}/items/${driveItem.parent_info.id}/activities`,
      //     api_action: 'get',
      //     api_payload: {},
      //   });

      //   updatedParentActivityLog = parentFolderActivityLogResponse.value.filter(log => log.action?.delete)
      //   .map((log: any) => ({
      //       actor: log.actor.user.displayName + ' Email: ' + log.actor.user.email,
      //       action: log.action,
      //       activityDateTime: log.times.recordedDateTime,
      //       logId: log.id,
      //   }));

      // }

      let transformedFileActivityLog = fileActivityLogResponse.value.map((log: any) => ({
        actor: log.actor.user.displayName + ' Email: ' + log.actor.user.email,
        action: log.action,
        activityDateTime: log.times.recordedDateTime,
        logId: log.id,
      }));

      // let mergedActivityLogs = transformedFileActivityLog;

      transformedFileActivityLog = transformedFileActivityLog.filter((log) => log !== undefined)
      .sort((a, b) => new Date(b.activityDateTime).getTime() - new Date(a.activityDateTime).getTime()); // sort latest first;


      return transformedFileActivityLog;

    } catch (error) {
      handleResponseException(error);
    }
  }

  // Helper functions
  const setUploadPreparation = (fileName: string, preparing: boolean) => {
    uploadPreparation.value = {
      ...uploadPreparation.value,
      [fileName]: preparing,
    };
  };

  const clearUploadPreparation = (fileName: string) => {
    const newUploadPreparation = { ...uploadPreparation.value };
    delete newUploadPreparation[fileName];
    uploadPreparation.value = newUploadPreparation;
  };

  // Add a function to handle path updates
  const updatePath = (newPath: string) => {
    if (!newPath) return;

    // Normalize the path
    const normalizedPath = newPath.startsWith('/') ? newPath : `/${newPath}`;

    // Update the parent info
    driveItemsParentInfo.value = {
      ...driveItemsParentInfo.value,
      path: normalizedPath,
    };

    // Trigger a refetch
    fetchSharePointFilesQuery.refetch();
  };

  // Return exposed state and methods
  return {
    // State
    isLoading,
    isOperationLoading,
    driveItems,
    driveItemsParentInfo,
    driveBreadCrumbs,
    activeFactory,
    sharePointDbSettingsNotFound,
    contextMenu,
    contextMenuRef,
    selectedFiles,
    isSelecting,
    selectionBox,
    uploadProgress,
    uploadPreparation,
    multiFileUpload,
    isUploading,
    itemActionInfo,
    moveOperationInProgress,
    isMovingItems,

    // Methods
    fetchSharePointFiles: () => fetchSharePointFilesQuery.refetch(),
    fetchFactorySharepointInfo: () => fetchFactorySharepointInfoQuery.refetch(),
    handleDriveItemAction,
    uploadSharepointFile,
    updateUploadProgress,
    clearFileUploadProgress,
    setMultiFileUpload,
    incrementCompletedFiles,
    setCancelToken,
    getFileLogs,
    searchSharepointFiles,
    getServerPreview,

    // Additional state for folder operations
    newFolderName,
    driveItemNewName,
    showCreateFolderModal,
    showDeleteConfirm,
    showRenameModal,
    handleSharepointDriveItemAction,
    handleDriveItemActionCompletion,

    // Additional state
    thumbnails,
    resetMultiFileUpload,
    getFileThumbnails,
    moveSharepointItem,
    cancelTokens,
    uploadLargeSharepointFile,
    removeCancelToken,
    setUploadPreparation,
    clearUploadPreparation,

    // Query states
    isFetchingFiles: fetchSharePointFilesQuery.isFetching,
    isFetchingFactoryInfo: fetchFactorySharepointInfoQuery.isFetching,
    filesError: fetchSharePointFilesQuery.error,
    factoryInfoError: fetchFactorySharepointInfoQuery.error,

    // Additional functions
    updatePath,
  };
};
